
import $ from 'jquery';
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Prop } from 'vue-property-decorator';
import { handleError, initClient } from '../../../../api';
import { formatDateTime, WindowType } from '../../../../util';
import { ActivityLog, Cell, CellClient, CellList, GpsPosDto, ICostSumup, Inventory, Line, Media, Media2, VMCellEdit } from '../../../backend/backendapi';
import MediaPicker from '../../../mediapicker/MediaPicker';
import TabCost from './tabs/TabCost';
import TabDuties from './tabs/TabDuties';
import TabGeneral from './tabs/TabGeneral';
import TabInventory from './tabs/TabInventory';
import { AiDocumentsClient, ReqCellMediaEnqueue } from "../../../backend/backendapi";
import DropZone from '../../../dropzone/DropZone';
import TabButton from '../../../util/TabButton';

declare var window: WindowType;

@Component({
  components: {
    DropZone,
    MediaPicker,
    TabButton,
    TabCost,
    TabDuties,
    TabGeneral,
    TabInventory,
    VueGoodTable,
    ImageViewer: require('../../../imageviewer/imageviewer.vue.html').default,
    MiscTab: require('./tabs/misc/misctab.vue.html').default,
    OeeTab: require('./tabs/oee/oeetab.vue.html').default,
    MeterTab: require('./tabs/meter/metertab.vue.html').default,
  },
  directives: {
    visible: require('../../../../directives/visible').default,
  },
})
export default class EditModal extends Vue {
  @Prop({ required: true })
  readonly lines!: Line[];
  @Prop({ required: true })
  readonly cells!: CellList[];
  @Prop({ required: true })
  readonly inventories!: Inventory[];

  //runtime
  cell: Cell | null = null;
  parentId: number | null = null;
  lastActivities: ActivityLog[] = [];
  plannedCosts: ICostSumup | null = null;
  unplannedCosts: ICostSumup | null = null;
  correctionCosts: ICostSumup | null = null;
  coords: GpsPosDto | null = null;
  aiActiveForUploads: boolean = false;

  //api
  readonly cellAPI = initClient(CellClient);
  readonly apiAi = initClient(AiDocumentsClient);

  tab: 'general' | 'cost' | 'attachments' | 'history' | 'coords' | 'inventories' | 'duties' | 'meter' | 'oee' | 'misc' = 'general';

  get columnsConfig(): unknown[] {
    return [
      {
        label: this.$t('activityDate').toString(),
        field: (e: ActivityLog) => e.currentDate,
        formatFn: (date?: Date) => date ? formatDateTime(date) : undefined,
      },
      {
        label: this.$t('activityTitle').toString(),
        field: 'title',
      },
      {
        label: this.$t('activityDescription').toString(),
        field: 'description',
      },
      {
        label: this.$t('activityUser').toString(),
        field: 'username',
      },
    ];
  }

  get canMap(): boolean {
    return window.application.check2.isMap;
  }

  get canMediaDelete(): boolean {
    return window.application.permissions.media.delete;
  }

  get canOee(): boolean {
    return window.application.check2.isOee;
  }

  get canDemo(): boolean {
    return window.application.check2.isDemo;
  }

  get canSave(): boolean {
    return !!(
        this.cell
        && this.cell.lineId
        && this.cell.name
    );
  }

  get isMaintenance(): boolean {
    return window.application.check2.isMaintenanceMode;
  };

  get isAiEnabled(): boolean { 
    return window.application.check2.isAi;
  }

  public open(cell: Cell, parentId: number | null): void {
    this.aiActiveForUploads=false;
    this.cell = cell;
    this.parentId = parentId;
    this.loadCellRelatedStuff(this.cell.id!);
    $(this.$refs.modal!).modal('show');
  }

  public dismiss(): void {
    $(this.$refs.modal!).modal('hide');
  }

  async loadCellRelatedStuff(cellId: number): Promise<void> {
    await Promise.all([
      this.fetchCellCosts(cellId),
      this.fetchCellActivities(cellId),
      this.fetchCellCoords(cellId),
    ]);
  }

  async fetchCellCosts(id: number): Promise<void> {
    this.plannedCosts = await this.cellAPI.getCellCost(id, 'planned').then(e => e.result!);
    this.unplannedCosts = await this.cellAPI.getCellCost(id, 'unplanned').then(e => e.result!);
    this.correctionCosts = await this.cellAPI.getCellCost(id, 'correction').then(e => e.result!);
  }

  async fetchCellActivities(id: number): Promise<void> {
    this.lastActivities.splice(0);
    this.lastActivities.push(...await this.cellAPI.getLastActivities(id).then(e => e.result!));
  }

  async fetchCellCoords(id: number): Promise<void> {
    this.coords = await this.cellAPI.getLastPos(id).then(e => e.result ?? null);
  }

  //actions
  openMediaPicker(): void {
    (this.$refs.mediapicker as MediaPicker).openCurrentFolder(() => {
    }, null);
  }

  copyCoordsToClipboard(): void {
    window.navigator.clipboard.writeText(this.coords?.lat + ', ' + this.coords?.lng);
    this.$toasted.info(this.$t('txtCopiedToClipboard').toString());
  }

  gotoAudit(): void {
    if (!this.cell) {
      return;
    }
    window.location.href = 'audit/log?filter=cellName="' + this.cell.name + '"';
  }

  //event
  onMediaPickerSelected(item: Media2): void {
    if (!this.cell) {
      return;
    }
    let newFile = new Media();
    newFile.id = item.id;
    newFile.fkCellId = this.cell.id;
    this.cellAPI.setMedia(newFile);
    this.$emit('change', this.cell.id!);
  }

  onDzComplete(file): void {
    if (!this.cell?.id) {
      return;
    }
    let response = JSON.parse(file.xhr.responseText);
    let newFile = new Media();
    newFile.id = response.fileId;
    newFile.fkCellId = this.cell.id;
    newFile.contentType = 'NA';
    newFile.filename = 'NA';
    this.cellAPI.setMedia(newFile).then( _ => {
      this.$toasted.info(this.$t('txtSaved').toString());
      if(this.isAiEnabled && this.aiActiveForUploads) {
        let req = new ReqCellMediaEnqueue();
        req.cellId = this.cell.id;
        req.mediaId = newFile.id;
        this.apiAi.media(req)
        .then(_ => {
          this.$toasted.info(this.$t('ai.resp.processing').toString());
          })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
      }
    });
    this.$emit('change', this.cell.id!);    
  }

  save(): Promise<void> {
    if (!this.cell)
      return Promise.resolve();

    let future;
    if (!this.cell.id) {
      let vm = new VMCellEdit();
      vm.cell = this.cell;
      vm.parentNodeId = this.parentId ?? undefined;
      future = this.cellAPI.postCellData(vm);
    } else {
      future = this.cellAPI.updateCellData(this.cell);
    }
    return future
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          this.dismiss();
          this.$emit('saved');
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }
}
